import { type ErrorInfo, Suspense, useState } from 'react';
import {
  type CoreLayoutProps,
  useCheckForApplicationUpdate,
  useLoading,
  useNotify,
  useStore,
  useTranslate,
} from 'ra-core';
import { LoaderCircle } from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSearchParams } from 'react-router-dom';
import { Error as ErrorPage } from '../components/admin/error';
import { Loading } from '../components/admin/loading';
import { Notification } from '../components/admin/notification';
import { SendbirdProviderWithSDK } from '../resources/Chat/SendbirdProviderWithSDK';
import { RefreshButton } from '../components/admin/refresh-button';
import { ThemeModeToggle } from '../components/admin/theme-mode-toggle';
import { Button } from '../components/ui/button';
import { Alert, AlertAction, AlertDescription } from '../components/ui/alert';
import { SidebarProvider, SidebarTrigger } from '../components/ui/sidebar';
import { cn } from '../lib/utils';
import { AppSidebar } from './AppSidebar';
import { UserMenu } from './UserMenu';

/**
 * App shell based on the kit `layout.tsx`.
 * The kit notification host is mounted once at the end of this shell.
 */
export const MoonstarLayout = ({ children }: CoreLayoutProps) => {
  const [searchParams] = useSearchParams();
  const hideSidebar = searchParams.get('hide_sidebar') === 'true';
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | undefined>(undefined);
  // Same store key as react-admin's `useSidebarState`, so the saved state survives reloads and the migration
  const [sidebarOpen, setSidebarOpen] = useStore('sidebar.open', true);

  return (
    <SendbirdProviderWithSDK>
      <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
        {!hideSidebar && <AppSidebar />}
        <main
          className={cn(
            'ml-auto w-full max-w-full',
            'peer-data-[state=collapsed]:w-[calc(100%-var(--sidebar-width-icon)-1rem)]',
            'peer-data-[state=expanded]:w-[calc(100%-var(--sidebar-width))]',
            'sm:transition-[width] sm:duration-200 sm:ease-linear',
            'flex h-svh flex-col bg-canvas',
            'group-data-[scroll-locked=1]/body:h-full',
            'has-[main.fixed-main]:group-data-[scroll-locked=1]/body:h-svh'
          )}
        >
          <header className="flex h-16 shrink-0 items-center gap-2 border-b bg-card px-4 md:h-12">
            {!hideSidebar && (
              <SidebarTrigger className="scale-125 sm:scale-100" />
            )}
            <div id="breadcrumb" className="flex flex-1 items-center" />
            <ThemeModeToggle />
            <MobileLoadingIndicator />
            <RefreshButton />
            <UserMenu />
          </header>
          <ErrorBoundary
            onError={(_, info) => setErrorInfo(info)}
            fallbackRender={({ error, resetErrorBoundary }) => (
              <ErrorPage
                error={error}
                errorInfo={errorInfo}
                resetErrorBoundary={resetErrorBoundary}
              />
            )}
          >
            <Suspense fallback={<Loading />}>
              <div
                className={cn(
                  'flex flex-1 flex-col',
                  hideSidebar ? 'p-0' : 'px-4 pt-4 pb-4'
                )}
              >
                {children}
              </div>
            </Suspense>
          </ErrorBoundary>
        </main>
        <Notification />
        <ApplicationUpdateChecker />
      </SidebarProvider>
    </SendbirdProviderWithSDK>
  );
};

/** The kit `RefreshButton` (which spins while loading) is hidden on small screens */
const MobileLoadingIndicator = () => {
  const loading = useLoading();
  const translate = useTranslate();

  return loading ? (
    <LoaderCircle
      className="size-4 animate-spin text-muted-foreground sm:hidden"
      role="status"
      aria-label={translate('ra.page.loading')}
    />
  ) : null;
};

const ApplicationUpdateChecker = () => {
  const notify = useNotify();
  const translate = useTranslate();

  useCheckForApplicationUpdate({
    onNewVersionAvailable: () => {
      notify(
        <Alert>
          <AlertDescription>
            {translate('ra.notification.application_update_available', {
              _: 'A new version of the application is available. Please update.',
            })}
          </AlertDescription>
          <AlertAction>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.location.reload()}
            >
              {translate('ra.action.update_application', {
                _: 'Update',
              })}
            </Button>
          </AlertAction>
        </Alert>,
        { type: 'info', autoHideDuration: null }
      );
    },
  });

  return null;
};
